import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { amount } from '../lib/format';
import '../styles/finance.css';

export interface LedgerRow {
  id: string; typeLabel: string; number: string; statusLabel: string;
  issueDate: number | null; counterparty: string | null;
  contract: { number: string; title: string | null } | null;
  currency: string; totalMinor: number; outstandingMinor: number;
}
export interface LedgerTotals {
  count: number; billedMinor: number; collectedMinor: number; pendingMinor: number;
  purchasedMinor: number; paidOutMinor: number; payableMinor: number; marginMinor: number;
}

const fmtDate = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString('es-ES') : '—');

/**
 * Informe del libro tal y como se imprime. Se usa tanto para verlo dentro de
 * la aplicación como para guardarlo en PDF desde el propio navegador, de modo
 * que funcione con o sin servidor detrás.
 */
export function AccountingReport({
  companyName, scope, period, totals, rows, currency, onClose, onServerPdf,
}: {
  companyName: string; scope: string; period: string;
  totals: LedgerTotals; rows: LedgerRow[]; currency: string;
  onClose: () => void; onServerPdf?: () => void;
}) {
  return (
    <div className="pdfv-scrim" onMouseDown={onClose}>
      <div className="pdfv" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pdfv__head rep-noprint">
          <span className="pdfv__title">Informe de contabilidad</span>
          <div className="row gap-8 center">
            {onServerPdf && <button className="btn btn--ghost btn--sm" onClick={onServerPdf}><Icon name="download" size={14} />Descargar PDF</button>}
            <button className="btn btn--primary btn--sm" onClick={() => window.print()}><Icon name="file" size={14} />Imprimir o guardar PDF</button>
            <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
        </div>

        <div className="pdfv__body" style={{ overflow: 'auto', background: '#fff' }}>
          <div className="rep" id="informe-contabilidad">
            <div className="rep__head">
              <div>
                <div className="rep__co">{companyName}</div>
                <div className="rep__meta">{scope} · {period} · {totals.count} movimientos</div>
              </div>
              <div className="rep__kicker">Informe de contabilidad</div>
            </div>

            <div className="rep__totals">
              <div><span>Facturado</span><b>{amount(totals.billedMinor, currency)}</b></div>
              <div><span>Cobrado</span><b>{amount(totals.collectedMinor, currency)}</b></div>
              <div><span>Pendiente de cobro</span><b>{amount(totals.pendingMinor, currency)}</b></div>
              <div><span>Compras y gastos</span><b>{amount(totals.purchasedMinor, currency)}</b></div>
              <div><span>Resultado</span><b>{amount(totals.marginMinor, currency)}</b></div>
            </div>

            <table className="rep__table">
              <thead>
                <tr>
                  <th>Fecha</th><th>Tipo</th><th>Number</th><th>Contraparte</th>
                  <th>Imputado a</th><th>Estado</th><th className="r">Total</th><th className="r">Pendiente</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.issueDate)}</td>
                    <td>{r.typeLabel}</td>
                    <td className="mono">{r.number}</td>
                    <td>{r.counterparty ?? '—'}</td>
                    <td>{r.contract?.number ?? 'Generales'}</td>
                    <td>{r.statusLabel}</td>
                    <td className="r mono">{amount(r.totalMinor, r.currency)}</td>
                    <td className="r mono">{amount(r.outstandingMinor, r.currency)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888' }}>Sin movimientos con estos filtros.</td></tr>}
              </tbody>
            </table>

            <div className="rep__foot">{companyName} · generado el {new Date().toLocaleString('es-ES')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Panel para compartir el resumen: copiar, WhatsApp o correo. */
export function AccountingShare({ text, onClose }: { text: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (copied) { const t = setTimeout(() => setCopied(false), 1800); return () => clearTimeout(t); } }, [copied]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); return; } catch { /* sin permiso */ }
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); setCopied(true); } catch { /* nada */ }
    ta.remove();
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal__head">
          <div><span className="eyebrow">Compartir</span><h2 className="h2" style={{ marginTop: 4, fontSize: 19 }}>Resumen de contabilidad</h2></div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>
        <div className="modal__body">
          <pre className="share-preview">{text}</pre>
          <div className="share-actions" style={{ marginTop: 14 }}>
            <a className="share-btn share-btn--wa" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">
              <span className="share-btn__ico"><Icon name="whatsapp" size={18} /></span>
              <span>WhatsApp<small>Enviar por chat</small></span>
            </a>
            <a className="share-btn share-btn--mail" href={`mailto:?subject=${encodeURIComponent('Resumen de contabilidad')}&body=${encodeURIComponent(text)}`}>
              <span className="share-btn__ico"><Icon name="mail" size={17} /></span>
              <span>Email<small>Abrir en tu cliente</small></span>
            </a>
            <button className="share-btn share-btn--copy" onClick={copy} style={{ gridColumn: '1 / -1' }}>
              <span className="share-btn__ico"><Icon name={copied ? 'check' : 'copy'} size={16} /></span>
              <span>{copied ? 'Copiado al portapapeles' : 'Copiar resumen'}<small>Paste it wherever you want</small></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
